import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Search, Store, Trophy } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import BrandLogo from '../components/BrandLogo';
import SupplierCard from '../components/SupplierCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { normalizeObjectStrings } from '../lib/utils';
import { SUPPLIERS_BETA_ENABLED } from '../lib/featureFlags';
import { BRAZIL_STATES, SUPPLIER_CATEGORIES } from '../lib/suppliers';
import { SUPPLIER_PREVIEW_ITEMS, getSupplierPreviewWithFallback } from '../lib/supplierPreviewContent';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;

const DIRECTORY_NAV = ['Meu casamento', 'Espacos', 'Fornecedores', 'Ideias', 'Comunidade'];

function normalizeValue(value) {
  return String(value || '').trim().toLowerCase();
}

export default function SupplierDirectoryPage() {
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ query: '', city: '', state: '', category: '' });

  useEffect(() => {
    if (!SUPPLIERS_BETA_ENABLED) return undefined;

    let active = true;
    setLoading(true);

    axios.get(`${API}/suppliers/public?limit=60`)
      .then((response) => {
        if (active) {
          const items = normalizeObjectStrings(response.data || []);
          setAllSuppliers((items.length > 0 ? items : SUPPLIER_PREVIEW_ITEMS).map(getSupplierPreviewWithFallback));
        }
      })
      .catch(() => {
        if (active) setAllSuppliers(SUPPLIER_PREVIEW_ITEMS.map(getSupplierPreviewWithFallback));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const filteredSuppliers = useMemo(() => {
    const query = normalizeValue(filters.query);
    const city = normalizeValue(filters.city);
    const state = normalizeValue(filters.state);
    const category = normalizeValue(filters.category);

    return allSuppliers.filter((supplier) => {
      const haystack = [
        supplier.public_name,
        supplier.company_name,
        supplier.category,
        supplier.city,
        supplier.state,
        supplier.short_description,
      ].map(normalizeValue).join(' ');

      if (query && !haystack.includes(query)) return false;
      if (city && normalizeValue(supplier.city) !== city) return false;
      if (state && normalizeValue(supplier.state) !== state) return false;
      if (category && normalizeValue(supplier.category) !== category) return false;
      return true;
    });
  }, [allSuppliers, filters]);

  const heroSupplier = useMemo(() => filteredSuppliers[0] || allSuppliers[0] || null, [filteredSuppliers, allSuppliers]);

  const categoryHighlights = useMemo(() => (
    SUPPLIER_CATEGORIES.slice(0, 4).map((category) => {
      const count = allSuppliers.filter((supplier) => normalizeValue(supplier.category) === normalizeValue(category)).length;
      const example = allSuppliers.find((supplier) => normalizeValue(supplier.category) === normalizeValue(category)) || heroSupplier;
      return {
        category,
        count,
        cover_image: example?.cover_image,
      };
    })
  ), [allSuppliers, heroSupplier]);

  const spotlightSuppliers = useMemo(() => (
    filteredSuppliers.slice(0, 4)
  ), [filteredSuppliers]);

  if (!SUPPLIERS_BETA_ENABLED) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-white text-[#1C1C1C]">
      <SEOHead
        title="Fornecedores | Virei Noiva"
        description="Encontre fornecedores para o seu casamento na vitrine de fornecedores do Virei Noiva."
        noindex
      />

      <header className="border-b border-[#E9E1DC] bg-white">
        <div className="container-wedding flex h-20 items-center justify-between gap-6">
          <div className="flex items-center gap-8">
            <Link to="/" className="inline-flex items-center">
              <BrandLogo size="lg" showTagline />
            </Link>
            <nav className="hidden items-center gap-6 lg:flex">
              {DIRECTORY_NAV.map((item) => (
                <span key={item} className={`text-[15px] font-medium ${item === 'Fornecedores' ? 'text-[#D94B63]' : 'text-[#1C1C1C]'}`}>
                  {item}
                </span>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="ghost" className="rounded-full px-4 text-[#1C1C1C] hover:bg-[#F8F4F1]">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar ao site
              </Button>
            </Link>
            <Link to="/fornecedor/login">
              <Button variant="ghost" className="rounded-full px-4 text-[#D94B63] hover:bg-[#FFF2F4] hover:text-[#D94B63]">
                Acesse
              </Button>
            </Link>
            <Link to="/fornecedor/cadastro">
              <Button variant="outline" className="rounded-full border-[#E9E1DC] bg-white px-5 text-[#1C1C1C] hover:bg-[#F8F4F1]">
                Registre-se
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="overflow-hidden border-b border-[#EFE8E2] bg-[#FCFAF8]">
          <div className="container-wedding grid min-h-[480px] items-stretch lg:grid-cols-[1.08fr_0.92fr]">
            <div className="relative flex items-center py-14 lg:py-20">
              <div className="relative z-10 max-w-[42rem] rounded-r-[8rem] bg-white px-0 py-0 lg:pr-16">
                <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#FFF2F4] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#D94B63]">
                  <Store className="h-3.5 w-3.5" />
                  Vitrine de fornecedores
                </p>
                <Link
                  to="/"
                  className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-[#7A6C65] transition hover:text-[#1C1C1C]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar para a home do Virei Noiva
                </Link>
                <h1 className="max-w-2xl text-5xl leading-[1.05] text-[#1F1A18] md:text-6xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Encontre tudo o que precisa para o seu casamento
                </h1>
                <p className="mt-6 text-2xl text-[#433B37]">
                  Mais de <span className="font-semibold">{allSuppliers.length || 82}</span> fornecedores para escolher.
                </p>

                <div className="mt-8 overflow-hidden rounded-2xl border border-[#E5DDD7] bg-white shadow-[0_20px_40px_-32px_rgba(28,28,28,0.35)]">
                  <div className="grid gap-0 md:grid-cols-[1.6fr_1fr_0.9fr_150px]">
                    <div className="flex items-center gap-3 px-5 py-4">
                      <Search className="h-5 w-5 text-[#7A6C65]" />
                      <Input
                        value={filters.query}
                        onChange={(event) => setFilters((prev) => ({ ...prev, query: event.target.value }))}
                        placeholder="Pesquise por nome ou categoria..."
                        className="border-0 bg-transparent p-0 text-base shadow-none focus-visible:ring-0"
                      />
                    </div>
                    <div className="border-t border-[#F0E6DF] px-5 py-4 md:border-l md:border-t-0">
                      <Input
                        value={filters.city}
                        onChange={(event) => setFilters((prev) => ({ ...prev, city: event.target.value }))}
                        placeholder="Cidade"
                        className="border-0 bg-transparent p-0 text-base shadow-none focus-visible:ring-0"
                      />
                    </div>
                    <div className="border-t border-[#F0E6DF] px-5 py-4 md:border-l md:border-t-0">
                      <select
                        value={filters.state}
                        onChange={(event) => setFilters((prev) => ({ ...prev, state: event.target.value }))}
                        className="w-full border-0 bg-transparent p-0 text-base text-[#1C1C1C] outline-none"
                      >
                        <option value="">Estado</option>
                        {BRAZIL_STATES.map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <Button
                      className="h-full rounded-none bg-[#E74B63] px-6 py-4 text-base font-semibold text-white hover:bg-[#d73d55]"
                      onClick={() => setFilters((prev) => ({ ...prev }))}
                    >
                      Pesquisar
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative min-h-[320px] overflow-hidden lg:min-h-full">
              <img
                src={heroSupplier?.cover_image || 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1400&q=80'}
                alt={heroSupplier?.public_name || 'Casamento em destaque'}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-white/30 lg:bg-gradient-to-r lg:from-transparent lg:to-transparent" />
            </div>
          </div>
        </section>

        <section className="container-wedding py-12">
          <div className="mb-6">
            <h2 className="text-4xl text-[#1F1A18]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Divirta-se organizando seu casamento
            </h2>
            <p className="mt-3 text-xl text-[#433B37]">
              Comece a explorar categorias, vitrines e fornecedores com mais facilidade.
            </p>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <article className="relative overflow-hidden rounded-[2rem] border border-[#EEE5DE] bg-[#FCFAF8]">
              <div className="grid min-h-[220px] md:grid-cols-[1fr_0.88fr]">
                <div className="relative z-10 flex flex-col justify-center px-6 py-8">
                  <h3 className="text-3xl text-[#1F1A18]" style={{ fontFamily: 'Playfair Display, serif' }}>Espacos para casamentos</h3>
                  <p className="mt-4 max-w-md text-lg leading-relaxed text-[#433B37]">
                    Navegue por fotos, opinioes e referencias visuais para descobrir o clima certo do seu evento.
                  </p>
                  <span className="mt-6 inline-flex items-center text-base font-semibold text-[#D94B63]">
                    Descubra os espacos
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
                <div className="relative">
                  <img
                    src="https://images.unsplash.com/photo-1522673607200-164d1b6ce486?auto=format&fit=crop&w=1200&q=80"
                    alt="Espacos para casamentos"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </article>

            <Link to="/fornecedores" className="block">
              <article className="relative overflow-hidden rounded-[2rem] border border-[#EEE5DE] bg-[#FCFAF8] transition hover:-translate-y-1">
              <div className="grid min-h-[220px] md:grid-cols-[1fr_0.88fr]">
                <div className="relative z-10 flex flex-col justify-center px-6 py-8">
                  <h3 className="text-3xl text-[#1F1A18]" style={{ fontFamily: 'Playfair Display, serif' }}>Fornecedores</h3>
                  <p className="mt-4 max-w-md text-lg leading-relaxed text-[#433B37]">
                    Encontre profissionais por categoria, localizacao e nivel de destaque dentro da vitrine de fornecedores.
                  </p>
                  <span className="mt-6 inline-flex items-center text-base font-semibold text-[#D94B63]">
                    Comece a sua pesquisa
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
                <div className="relative">
                  <img
                    src="https://images.unsplash.com/photo-1469371670807-013ccf25f16a?auto=format&fit=crop&w=1200&q=80"
                    alt="Fornecedores"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
              </article>
            </Link>
          </div>
        </section>

        <section className="container-wedding py-6">
          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#111111] text-white">
                <Trophy className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8D7F77]">awards</p>
                <h2 className="text-2xl text-[#1F1A18]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Consulte os vencedores da sua regiao
                </h2>
              </div>
            </div>
            <Button variant="outline" className="rounded-full border-[#E5DDD7] bg-white text-[#1C1C1C] hover:bg-[#F8F4F1]">
              Veja todos os ganhadores
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          <div className="grid gap-5 xl:grid-cols-4">
            {spotlightSuppliers.map((supplier) => (
              <SupplierCard key={supplier.supplier_id} supplier={supplier} />
            ))}
          </div>
        </section>

        <section className="container-wedding py-12">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8D7F77]">Empresas de casamento destacadas</p>
              <h2 className="mt-2 text-4xl text-[#1F1A18]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Resultados para sua busca
              </h2>
              <p className="mt-3 text-[#433B37]">
                {loading ? 'Carregando...' : `${filteredSuppliers.length} fornecedores encontrados`}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <select
                value={filters.category}
                onChange={(event) => setFilters((prev) => ({ ...prev, category: event.target.value }))}
                className="h-11 rounded-full border border-[#E5DDD7] bg-white px-4 text-sm text-[#1C1C1C] outline-none"
              >
                <option value="">Todas as categorias</option>
                {SUPPLIER_CATEGORIES.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <Button
                variant="ghost"
                className="rounded-full text-[#D94B63] hover:bg-[#FFF2F4] hover:text-[#D94B63]"
                onClick={() => setFilters({ query: '', city: '', state: '', category: '' })}
              >
                Limpar filtros
              </Button>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {loading ? (
              <div className="col-span-full rounded-[2rem] border border-[#EEE5DE] bg-[#FCFAF8] p-10 text-center text-[#5A4C49]">
                Carregando fornecedores...
              </div>
            ) : filteredSuppliers.length === 0 ? (
              <div className="col-span-full rounded-[2rem] border border-[#EEE5DE] bg-[#FCFAF8] p-10 text-center">
                <Search className="mx-auto h-10 w-10 text-[#C8A97E]" />
                <p className="mt-4 text-[#5A4C49]">Nenhum fornecedor encontrado com esses filtros.</p>
              </div>
            ) : (
              filteredSuppliers.map((supplier) => (
                <SupplierCard key={supplier.supplier_id} supplier={supplier} />
              ))
            )}
          </div>
        </section>

        <section className="container-wedding pb-16">
          <div className="grid gap-5 lg:grid-cols-4">
            {categoryHighlights.map((item) => (
              <button
                type="button"
                key={item.category}
                onClick={() => setFilters((prev) => ({ ...prev, category: item.category }))}
                className="overflow-hidden rounded-[1.6rem] border border-[#EEE5DE] bg-[#FCFAF8] text-left transition hover:-translate-y-1"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={item.cover_image || heroSupplier?.cover_image}
                    alt={item.category}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="space-y-3 p-5">
                  <h3 className="text-2xl text-[#1F1A18]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {item.category}
                  </h3>
                  <p className="text-sm leading-relaxed text-[#433B37]">
                    {item.count > 0 ? `${item.count} fornecedores em destaque nessa categoria.` : 'Explore fornecedores dessa categoria na vitrine.'}
                  </p>
                  <span className="inline-flex items-center text-sm font-semibold text-[#D94B63]">
                    Ver categoria
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
